import { useState, useEffect, useCallback } from 'react';
import { validateCart } from '../api/cart';
import { formatStockStatus, getAllLocationStatus } from '../utils/locationUtils';

export const useCartValidation = (cartItems) => {
    const [validatedCart, setValidatedCart] = useState([]);
    const [isValidating, setIsValidating] = useState(false);
    const [lastValidation, setLastValidation] = useState(0);
    const [hasInitialized, setHasInitialized] = useState(false);

    const validateCartItems = useCallback(async (force = false) => {
        if (!cartItems?.length) {
            setValidatedCart([]);
            return;
        }

        // If cartItems has items but validatedCart is empty, initialize with cartItems
        if (cartItems?.length > 0 && (!validatedCart || validatedCart.length === 0)) {
            setValidatedCart(cartItems.map(item => {
                const stockInfo = formatStockStatus(item);
                return {
                    ...item,
                    isAvailable: stockInfo.hasStock,
                    currentPrice: item.Sale,
                    originalPrice: item.Purchase,
                    stock: item.Stock,
                    hasDiscount: false,
                    validationError: false,
                    stockInfo: stockInfo,
                    locationStatus: getAllLocationStatus(item)
                };
            }));
        }

        // Check if we need to validate (cache for 5 minutes unless forced)
        const now = Date.now();
        if (!force && (now - lastValidation) < 5 * 60 * 1000) {
            return;
        }

        setIsValidating(true);

        try {
            // Prepare cart items for API request
            const cartItemsForValidation = cartItems.map(item => ({
                ID_Info: item.ID_Info,
                Name: item.Name,
                Platform: item.Platform,
                Purchase: item.Purchase,
                Exchange: item.Exchange,
                Sale: item.Sale,
                Quantity: item.Quantity,
                ...(item.retroGameID && { retroGameID: item.retroGameID })
            }));

            // Call the new validation API
            const validationResponse = await validateCart(cartItemsForValidation);

            if (!validationResponse || !Array.isArray(validationResponse)) {
                throw new Error('Invalid validation response');
            }

            // Create a map of validation results by ID_Info
            const validationMap = new Map(
                validationResponse.map(result => [String(result.ID_Info), result.inStock])
            );

            // Map cart items with validation results
            const validatedItems = cartItems.map(item => {
                const isAvailable = validationMap.get(String(item.ID_Info)) ?? false;
                const stockInfo = formatStockStatus(item);

                return {
                    ...item,
                    isAvailable: isAvailable,
                    currentPrice: item.Sale,
                    originalPrice: item.Purchase,
                    stock: item.Stock,
                    hasDiscount: false,
                    validationError: !validationMap.has(String(item.ID_Info)),
                    stockInfo: stockInfo,
                    locationStatus: getAllLocationStatus(item)
                };
            });

            setValidatedCart(validatedItems);
            setLastValidation(now);

            // Store validation timestamp in localStorage
            localStorage.setItem('lastCartValidation', now.toString());

        } catch (error) {
            console.error('Error validating cart:', error);
            // Fallback to original cart items with location info
            setValidatedCart(cartItems.map(item => {
                const stockInfo = formatStockStatus(item);
                return {
                    ...item,
                    isAvailable: stockInfo.hasStock,
                    currentPrice: item.Sale,
                    originalPrice: item.Purchase,
                    stock: item.Stock,
                    hasDiscount: false,
                    validationError: true,
                    stockInfo: stockInfo,
                    locationStatus: getAllLocationStatus(item)
                };
            }));
        } finally {
            setIsValidating(false);
        }
    }, [cartItems, lastValidation]);

    // Keep validatedCart in sync with cartItems (immediate UI feedback for add/remove actions)
    useEffect(() => {
        if (!cartItems?.length) {
            setValidatedCart(prev => (prev.length > 0 ? [] : prev));
            return;
        }

        setValidatedCart(prev => {
            const prevMap = new Map((prev || []).map(item => [item.ID_Info, item]));
            let didChange = false;

            const updated = cartItems.map(item => {
                const existing = prevMap.get(item.ID_Info);

                if (existing) {
                    // Ensure quantity and other mutable fields stay in sync
                    if (
                        existing.Quantity !== item.Quantity ||
                        existing.typeOfBox !== item.typeOfBox
                    ) {
                        didChange = true;
                        return {
                            ...existing,
                            Quantity: item.Quantity,
                            typeOfBox: item.typeOfBox
                        };
                    }
                    return existing;
                }

                didChange = true;
                const stockInfo = formatStockStatus(item);
                return {
                    ...item,
                    isAvailable: stockInfo.hasStock,
                    currentPrice: item.Sale,
                    originalPrice: item.Purchase,
                    stock: item.Stock,
                    hasDiscount: false,
                    validationError: false,
                    stockInfo: stockInfo,
                    locationStatus: getAllLocationStatus(item)
                };
            });

            if (!didChange && prev && prev.length === updated.length) {
                return prev;
            }

            return updated;
        });
    }, [cartItems]);

    // Initialize validatedCart when cartItems changes
    useEffect(() => {
        if (cartItems?.length > 0 && (!validatedCart || validatedCart.length === 0) && !hasInitialized) {
            // console.log('useCartValidation: Initializing validatedCart with cartItems:', cartItems.length);
            setValidatedCart(cartItems.map(item => {
                const stockInfo = formatStockStatus(item);
                return {
                    ...item,
                    isAvailable: stockInfo.hasStock,
                    currentPrice: item.Sale,
                    originalPrice: item.Purchase,
                    stock: item.Stock,
                    hasDiscount: false,
                    validationError: false,
                    stockInfo: stockInfo,
                    locationStatus: getAllLocationStatus(item)
                };
            }));

            setHasInitialized(true);

            // Automatically validate cart items when they're first loaded
            // console.log('useCartValidation: Auto-validating cart items on load');
            // Use setTimeout to break the dependency cycle
            setTimeout(() => {
                validateCartItems(true); // Force validation
            }, 100);
        }
    }, [cartItems, validatedCart, hasInitialized]); // Added hasInitialized to prevent re-triggering

    // Validate on mount and when cart items change
    useEffect(() => {
        // console.log('useCartValidation: useEffect triggered, cartItems length:', cartItems?.length);
        if (cartItems?.length > 0) {
            // Use setTimeout to break the dependency cycle
            setTimeout(() => {
                validateCartItems();
            }, 100);
        }
    }, [cartItems]); // Removed validateCartItems from dependencies

    // Check localStorage for last validation time on mount
    useEffect(() => {
        const stored = localStorage.getItem('lastCartValidation');
        if (stored) {
            setLastValidation(parseInt(stored));
        }

        // Auto-validate on mount if we have cart items
        if (cartItems?.length > 0 && !hasInitialized) {
            // console.log('useCartValidation: Auto-validating on mount with', cartItems.length, 'items');
            // Use setTimeout to break the dependency cycle
            setTimeout(() => {
                validateCartItems(true);
            }, 100);
        }
    }, [cartItems]); // Removed validateCartItems from dependencies

    return {
        validatedCart,
        isValidating,
        validateCartItems,
        lastValidation
    };
};
